import { Link } from 'react-router-dom';
import type { Topic } from '@/types/content';
import { formatDate, formatHeat } from '@/utils/format';

interface TopicCardProps {
  topic: Topic;
  layout?: 'vertical' | 'horizontal';
}

export default function TopicCard({ topic, layout = 'vertical' }: TopicCardProps) {
  const isHorizontal = layout === 'horizontal';

  return (
    <article
      className={`group bg-background-50 border border-background-200/80 rounded-lg overflow-hidden hover:border-primary-300 transition-colors ${
        isHorizontal ? 'flex flex-col sm:flex-row' : 'flex flex-col h-full'
      }`}
    >
      <Link
        to={`/topic/${topic.id}`}
        className={`block overflow-hidden bg-background-200 ${
          isHorizontal ? 'w-full sm:w-56 md:w-64 shrink-0' : 'w-full'
        }`}
      >
        <div
          className={`w-full overflow-hidden ${
            isHorizontal ? 'h-44 sm:h-full sm:min-h-[176px]' : 'h-48 md:h-52'
          }`}
        >
          <img
            src={topic.image}
            alt={`${topic.title}现场记录配图`}
            title={`${topic.title} · 511群众吃瓜围观`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </div>
      </Link>

      <div className={`flex flex-col p-4 md:p-5 ${isHorizontal ? 'flex-1' : 'flex-1'}`}>
        <div className="flex items-center gap-2 mb-2.5">
          <Link
            to={`/frontline?category=${topic.category}`}
            className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap"
          >
            {topic.categoryLabel}
          </Link>
          <span className="text-xs text-foreground-500 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-fire-line text-primary-500"></i>
            {formatHeat(topic.heat)}
          </span>
        </div>

        <h3 className="font-heading font-bold text-base md:text-lg leading-snug text-foreground-950">
          <Link to={`/topic/${topic.id}`} className="hover:text-primary-700 transition-colors">
            {topic.title}
          </Link>
        </h3>

        <p className={`mt-2 text-sm leading-relaxed text-foreground-600 ${isHorizontal ? 'line-clamp-3' : 'line-clamp-2'}`}>
          {topic.summary}
        </p>

        <div className="mt-auto pt-4 flex items-center justify-between gap-3 text-xs text-foreground-500">
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-800 shrink-0">
              <i className="ri-user-smile-line"></i>
            </span>
            <span className="truncate">{topic.author}</span>
          </span>
          <span className="flex items-center gap-3 shrink-0">
            <span className="whitespace-nowrap">{formatDate(topic.date)}</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-chat-3-line"></i>
              {topic.comments}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}